import {
	Button as MuiButton,
	ButtonProps as ButtonPropsFrom,
	Link,
	LinkProps as MuiLinkProps,
} from '@mui/material';
import React from 'react';
import classNames from 'classnames';

/* Button is a general-purpose button replacement for our RPC styled-component buttons.
It should handle switching to a navigational <a> tag when href is supplied, and a <button>
otherwise, so that nextjs is happy and we don't get total page reloads just to navigate
client-side, improving perceived performance */

type LinkProps = Pick<
ButtonPropsFrom,
'startIcon' | 'variant' | 'color' | 'disabled' | 'loading'
> &
Pick<
React.DetailedHTMLProps<
React.AnchorHTMLAttributes<HTMLAnchorElement>,
HTMLAnchorElement
>,
'className' | 'ref' | 'children' | 'target' | 'rel'
> & { href: NonNullable<MuiLinkProps['href']> };

type ButtonProps = Pick<
ButtonPropsFrom,
| 'onClick'
| 'disabled'
| 'variant'
| 'className'
| 'ref'
| 'color'
| 'children'
| 'startIcon'
| 'type'
| 'sx'
| 'loading'
>;

export type ButtonOrLinkProps = ( LinkProps | ButtonProps ) & {
	// Props common to both link and button versions of this component
	id?: string;
	'data-cy'?: string;
};

/* non-navigational buttons should be buttons, and navigational "buttons" should be links
The MUI "Link" claims that if there's no href on it it spits out a button, but I did not find that to be true */
export const Button: React.FC<ButtonOrLinkProps> = ( props ) => {
	if ( 'href' in props && props.href ) {
		const { ref, href, children, variant, className, ...linkProps } = props;
		return (
			<MuiButton
				component={ Link }
				href={ href }
				ref={ ref as React.Ref<HTMLAnchorElement> }
				variant={ variant || 'contained' }
				className={ classNames( className, !children && 'empty' ) }
				{ ...linkProps }
			>
				{ children }
			</MuiButton>
		);
	} else if ( !( 'href' in props ) ) {
		// either onClick or href need to be passed in
		const { ref, className, variant, children, ...buttonProps } = props;
		return (
			<MuiButton
				ref={ ref }
				variant={ variant ? variant : 'contained' }
				className={ classNames( className, !children && 'empty' ) }
				{ ...buttonProps }
			>
				{ children }
			</MuiButton>
		);
	} else {
		return null;
	}
};
